import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Typography,
  Box,
  Avatar,
  IconButton,
  InputAdornment,
  Chip
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { closeModal } from '../../../store/slices/uiSlice';
import { createInvitation, resetInvitationState, setInvitationFilters } from '../../../store/slices/invitationSlice';
import { ErrorMessage } from '../../common';
import api from '../../../services/api';

const SPECIALIZATIONS = [
  'Vedic Astrology',
  'Numerology',
  'Tarot Reading',
  'Palmistry',
  'Vastu Shastra',
  'KP Astrology',
  'Prashna Kundli',
  'Lal Kitab',
  'Western Astrology',
  'Face Reading',
  'General'
];

const EXPERTISE_OPTIONS = [
  'Kundli', 'Marriage', 'Career', 'Love', 'Finance', 'Health',
  'Business', 'Education', 'Property', 'Foreign Travel', 'Gemstone', 'Muhurat'
];

const LANGUAGE_OPTIONS = [
  'Hindi', 'English', 'Tamil', 'Telugu', 'Kannada', 'Malayalam',
  'Bengali', 'Marathi', 'Gujarati', 'Punjabi', 'Odia', 'Urdu'
];

const validationSchema = Yup.object({
  email: Yup.string().email('Invalid email address').required('Email is required'),
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  specialization: Yup.string().required('Specialization is required'),
  experienceYears: Yup.number().integer().min(0, 'Min 0').max(50, 'Max 50').required('Required'),
  bio: Yup.string().min(10, 'At least 10 characters').required('Bio is required'),
  pricePerMinuteChat: Yup.number().min(1, 'Min 1').required('Chat rate is required'),
  pricePerMinuteCall: Yup.number().min(1, 'Min 1').required('Call rate is required'),
  pricePerMinuteVideo: Yup.number().min(1, 'Min 1').required('Video rate is required'),
  expertise: Yup.array().max(3, 'Max 3 expertise areas'),
  expiresDays: Yup.number().integer().min(1).max(30).required('Required'),
  notes: Yup.string().max(500)
});

const CreateInvitationModal = () => {
  const dispatch = useDispatch();
  const { error, filters = { status: 'all', page: 1, limit: 10 } } = useSelector(state => state.invitations);
  const { modals } = useSelector(state => state.ui);
  const open = modals.createInvitation;
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleClose = () => {
    dispatch(resetInvitationState());
    dispatch(closeModal('createInvitation'));
    setImagePreview(null);
  };

  const handleImageUpload = async (e, setFieldValue) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a JPG, PNG, or WebP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    // Show local preview
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);

    // Upload to server
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await api.post('/admin/upload/profile-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFieldValue('profileImageUrl', response.data.url);
    } catch (err) {
      console.error('Image upload failed:', err);
      alert('Failed to upload image. Please try again.');
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = {
        ...values,
        expertise: (values.expertise || []).join(','),
        languages: (values.languages || []).join(','),
      };
      await dispatch(createInvitation(payload)).unwrap();
      resetForm();
      handleClose();
      dispatch(setInvitationFilters({ ...filters, _refresh: Date.now() }));
    } catch (error) {
      console.error('Error creating invitation:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Astrologer Invitation</DialogTitle>

      <Formik
        initialValues={{
          email: '',
          firstName: '',
          lastName: '',
          profileImageUrl: '',
          specialization: '',
          experienceYears: 1,
          bio: '',
          pricePerMinuteChat: 10,
          pricePerMinuteCall: 15,
          pricePerMinuteVideo: 20,
          expertise: [],
          languages: [],
          expiresDays: 7,
          notes: ''
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched, handleChange, handleBlur, values, setFieldValue }) => (
          <Form>
            <DialogContent>
              {error && <ErrorMessage message={error} />}

              {/* Profile Image */}
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar
                  src={imagePreview || ''}
                  sx={{ width: 80, height: 80, bgcolor: '#e0e0e0' }}
                >
                  {values.firstName?.[0]?.toUpperCase() || '?'}
                </Avatar>
                <Box>
                  <input
                    accept="image/jpeg,image/png,image/webp"
                    style={{ display: 'none' }}
                    id="profile-image-upload"
                    type="file"
                    onChange={(e) => handleImageUpload(e, setFieldValue)}
                  />
                  <label htmlFor="profile-image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={uploadingImage ? <CircularProgress size={16} /> : <PhotoCameraIcon />}
                      disabled={uploadingImage}
                      size="small"
                    >
                      {uploadingImage ? 'Uploading...' : 'Upload Photo'}
                    </Button>
                  </label>
                  <Typography variant="caption" display="block" color="text.secondary">
                    JPG, PNG or WebP. Max 5MB.
                  </Typography>
                </Box>
              </Box>

              {/* Astrologer Info */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Astrologer Info
              </Typography>
              <Box display="flex" gap={2} mb={1}>
                <TextField
                  name="firstName"
                  label="First Name"
                  fullWidth
                  margin="dense"
                  value={values.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.firstName && Boolean(errors.firstName)}
                  helperText={touched.firstName && errors.firstName}
                />
                <TextField
                  name="lastName"
                  label="Last Name"
                  fullWidth
                  margin="dense"
                  value={values.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.lastName && Boolean(errors.lastName)}
                  helperText={touched.lastName && errors.lastName}
                />
              </Box>

              <TextField
                name="email"
                label="Email Address"
                fullWidth
                margin="dense"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />

              {/* Professional Details */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }} gutterBottom>
                Professional Details
              </Typography>
              <Box display="flex" gap={2} mb={1}>
                <TextField
                  select
                  name="specialization"
                  label="Specialization"
                  fullWidth
                  margin="dense"
                  value={values.specialization}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.specialization && Boolean(errors.specialization)}
                  helperText={touched.specialization && errors.specialization}
                >
                  {SPECIALIZATIONS.map(s => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  name="experienceYears"
                  label="Experience (years)"
                  type="number"
                  fullWidth
                  margin="dense"
                  value={values.experienceYears}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.experienceYears && Boolean(errors.experienceYears)}
                  helperText={touched.experienceYears && errors.experienceYears}
                />
              </Box>

              <TextField
                name="bio"
                label="About the Astrologer"
                multiline
                rows={3}
                fullWidth
                margin="dense"
                value={values.bio}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.bio && Boolean(errors.bio)}
                helperText={touched.bio && errors.bio}
              />

              {/* Expertise Tags */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }} gutterBottom>
                Expertise (up to 3)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                {EXPERTISE_OPTIONS.map(tag => {
                  const selected = (values.expertise || []).includes(tag);
                  return (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => {
                        const current = values.expertise || [];
                        if (selected) {
                          setFieldValue('expertise', current.filter(t => t !== tag));
                        } else if (current.length < 3) {
                          setFieldValue('expertise', [...current, tag]);
                        }
                      }}
                      sx={{ cursor: 'pointer' }}
                    />
                  );
                })}
              </Box>
              {touched.expertise && errors.expertise && (
                <Typography variant="caption" color="error">{errors.expertise}</Typography>
              )}

              {/* Languages */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }} gutterBottom>
                Languages
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                {LANGUAGE_OPTIONS.map(lang => {
                  const selected = (values.languages || []).includes(lang);
                  return (
                    <Chip
                      key={lang}
                      label={lang}
                      size="small"
                      color={selected ? 'secondary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => {
                        const current = values.languages || [];
                        if (selected) {
                          setFieldValue('languages', current.filter(l => l !== lang));
                        } else {
                          setFieldValue('languages', [...current, lang]);
                        }
                      }}
                      sx={{ cursor: 'pointer' }}
                    />
                  );
                })}
              </Box>

              {/* Rates */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }} gutterBottom>
                Rates (per minute)
              </Typography>
              <Box display="flex" gap={2} mb={1}>
                <TextField
                  name="pricePerMinuteChat"
                  label="Chat"
                  type="number"
                  fullWidth
                  margin="dense"
                  value={values.pricePerMinuteChat}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.pricePerMinuteChat && Boolean(errors.pricePerMinuteChat)}
                  helperText={touched.pricePerMinuteChat && errors.pricePerMinuteChat}
                  InputProps={{ startAdornment: <InputAdornment position="start">&#8377;</InputAdornment> }}
                />
                <TextField
                  name="pricePerMinuteCall"
                  label="Voice Call"
                  type="number"
                  fullWidth
                  margin="dense"
                  value={values.pricePerMinuteCall}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.pricePerMinuteCall && Boolean(errors.pricePerMinuteCall)}
                  helperText={touched.pricePerMinuteCall && errors.pricePerMinuteCall}
                  InputProps={{ startAdornment: <InputAdornment position="start">&#8377;</InputAdornment> }}
                />
                <TextField
                  name="pricePerMinuteVideo"
                  label="Video Call"
                  type="number"
                  fullWidth
                  margin="dense"
                  value={values.pricePerMinuteVideo}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.pricePerMinuteVideo && Boolean(errors.pricePerMinuteVideo)}
                  helperText={touched.pricePerMinuteVideo && errors.pricePerMinuteVideo}
                  InputProps={{ startAdornment: <InputAdornment position="start">&#8377;</InputAdornment> }}
                />
              </Box>

              {/* Settings */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }} gutterBottom>
                Invitation Settings
              </Typography>
              <TextField
                select
                name="expiresDays"
                label="Expires In"
                fullWidth
                margin="dense"
                value={values.expiresDays}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.expiresDays && Boolean(errors.expiresDays)}
                helperText={touched.expiresDays && errors.expiresDays}
              >
                <MenuItem value={1}>1 day</MenuItem>
                <MenuItem value={3}>3 days</MenuItem>
                <MenuItem value={7}>7 days</MenuItem>
                <MenuItem value={14}>14 days</MenuItem>
                <MenuItem value={30}>30 days</MenuItem>
              </TextField>

              <TextField
                name="notes"
                label="Notes (Internal only)"
                multiline
                rows={2}
                fullWidth
                margin="dense"
                value={values.notes}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.notes && Boolean(errors.notes)}
                helperText={touched.notes && errors.notes}
              />
            </DialogContent>

            <DialogActions>
              <Button onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting || uploadingImage}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              >
                {isSubmitting ? 'Sending...' : 'Send Invitation'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CreateInvitationModal;
